import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  Image as ImageLib,
  Inbox as InboxIcon,
  Lightbulb,
  Sparkles,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface QuickActionsProps {
  brandSlug: string;
  /** null when the brand has no inbox (no Instagram/Facebook account). */
  unreadTotal: number | null;
}

interface Action {
  key: "quickPost" | "compose" | "calendar" | "inbox" | "library" | "ideas";
  to: string;
  icon: LucideIcon;
  /** Tailwind classes for the icon chip — each action gets its own color so it's findable at a glance. */
  tone: string;
  primary?: boolean;
}

/** The things people come to Pilot to do, one click away. */
export function HomeQuickActions({ brandSlug, unreadTotal }: QuickActionsProps) {
  const { t } = useTranslation("dashboard");
  const prefix = `/${brandSlug}`;

  const actions: Action[] = [
    { key: "quickPost", to: `${prefix}/quick-post`, icon: Zap, tone: "bg-primary text-primary-foreground", primary: true },
    { key: "compose", to: `${prefix}/compose`, icon: Sparkles, tone: "bg-violet-500/15 text-violet-600 dark:text-violet-400" },
    { key: "calendar", to: `${prefix}/calendar`, icon: CalendarDays, tone: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
    ...(unreadTotal !== null
      ? [{ key: "inbox", to: `${prefix}/inbox`, icon: InboxIcon, tone: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" } as Action]
      : []),
    { key: "library", to: `${prefix}/library`, icon: ImageLib, tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
    { key: "ideas", to: `${prefix}/ideas`, icon: Lightbulb, tone: "bg-rose-500/15 text-rose-600 dark:text-rose-400" },
  ];

  return (
    <section aria-labelledby="home-quick-actions">
      <h2 id="home-quick-actions" className="sr-only">
        {t("home.quickActions.title")}
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {actions.map(({ key, to, icon: Icon, tone, primary }) => {
          const badge = key === "inbox" && unreadTotal ? unreadTotal : 0;
          return (
            <Link
              key={key}
              to={to}
              className={cn(
                "group relative flex flex-col gap-3 rounded-xl border bg-card p-4 transition",
                "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                primary && "border-primary/40 bg-primary/5",
              )}
            >
              <span className={cn("flex h-9 w-9 items-center justify-center rounded-lg", tone)}>
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <span className="space-y-0.5">
                <span className="block text-sm font-semibold leading-tight">
                  {t(`home.quickActions.${key}.label`)}
                </span>
                <span className="block text-xs text-muted-foreground leading-snug">
                  {t(`home.quickActions.${key}.desc`)}
                </span>
              </span>
              {badge > 0 && (
                <span className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-primary-foreground">
                  {t("home.quickActions.newCount", { count: badge })}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
